// =============================================================================
// Optim TURPE V2 — LE CHIFFRE (DEV-1913, décisions A3 et A4).
//
// Ce que le client doit vivre : un seul montant en tête, l'économie NETTE sur la
// période analysée, toujours décomposée en « structure (ferme, démontrable) » et
// « dépassements évités ou provoqués (à confirmer) » ; sa durée à côté ; et
// l'équivalent annuel pour information quand le moteur le sert.
//
// Deux règles tiennent tout ce fichier :
//
// 1. **La page ne décide plus l'état.** `restitution.etat` est calculé par le
//    moteur sur la même grandeur que le chiffre (DEV-1907). L'écran le LIT.
//    Sur une étude antérieure à ce lot, l'état est déduit du montant servi par
//    la règle publiée (§2.3 du contrat cible) et la page dit qu'il est déduit —
//    elle n'invente pas un verdict et ne garde plus de seuil comme source.
// 2. **Aucun montant n'est recalculé ici.** Tout euro affiché vient d'un champ.
//    Un champ absent est une absence DITE, jamais un 0. Y compris l'équivalent
//    annuel : il est calculé par le moteur avec le bon facteur, ou il n'est pas
//    affiché.
// =============================================================================

import { SEUIL_ETAT_EUR_REPLI } from "@modules/courtage/constants/optim-turpe-v2"
import type {
    OptimTurpeV2EquivalentAnnuel,
    OptimTurpeV2EtatServi,
    OptimTurpeV2Etude,
    OptimTurpeV2Reconciliation,
} from "@modules/courtage/types/optim-turpe-v2"

/** Les états sous lesquels le chiffre annoncé est un GAIN. */
export const ETATS_DE_GAIN: OptimTurpeV2EtatServi[] = [
    "economie",
    "gain_par_depassements_evites",
]

/** Un nombre fini, ou `null`. Ni `NaN`, ni `Infinity`, ni une chaîne. */
function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** Durée en jours calendaires entre deux dates ISO incluses, ou `null`. */
function joursEntre(
    dateFrom: string | null | undefined,
    dateTo: string | null | undefined
): number | null {
    if (!dateFrom || !dateTo) return null
    const debut = Date.parse(`${dateFrom}T00:00:00Z`)
    const fin = Date.parse(`${dateTo}T00:00:00Z`)
    if (Number.isNaN(debut) || Number.isNaN(fin) || fin < debut) return null
    return Math.round((fin - debut) / 86_400_000) + 1
}

/** La période servie, ou son repli lu sur les dates demandées. */
export type PeriodeLue = {
    dateFrom: string | null
    dateTo: string | null
    jours: number | null
    pasPrices: number | null
    estAnnuelle: boolean
    /** Vrai quand `jours` vient du calendrier et non de `periode.jours`. */
    joursDerive: boolean
    /** Vrai quand le moteur sert `restitution.periode`. */
    servie: boolean
}

/**
 * La décomposition de l'économie. `disponible` est faux quand le moteur ne sert
 * pas `depassements_eur` : on ne le DÉDUIT pas de `nette − ferme`, parce qu'un
 * montant affiché doit venir d'un champ du moteur. La page dit alors que la
 * décomposition n'est pas servie, et garde la nette comme chiffre de référence.
 */
export type DecompositionLue = {
    disponible: boolean
    fermeEur: number | null
    depassementsEur: number | null
}

export type ChiffreLu = {
    /** L'état de l'étude. `null` = information non disponible, à dire dans la donnée. */
    etat: OptimTurpeV2EtatServi | null
    /** Vrai quand l'état vient du moteur ; faux quand il est déduit du montant servi. */
    etatServiParLeMoteur: boolean
    /** Motif du surcoût, servi par le moteur (`scenario_libre`, `couverture_du_pic`, …). */
    etatMotif: string | null
    /** Cause d'un état absent, servie par le moteur (`economie-non-calculable`, …). */
    etatCause: string | null
    /** Seuil qui a séparé « économie » de « déjà optimale ». */
    seuilEur: number | null
    seuilServiParLeMoteur: boolean
    /** Le chiffre PRINCIPAL : économie nette de la période. `null` = non servi. */
    netteEur: number | null
    decomposition: DecompositionLue
    /** Régime du montant, imprimé tel quel (`HTVA`) — jamais un libellé en dur. */
    regimeFiscal: string | null
    /** Le montant est-il servi (A9) ? `null` = le moteur ne l'instruit pas encore. */
    montantServi: boolean | null
    montantNonServiCause: string | null
    periode: PeriodeLue
    /** Équivalent annuel POUR INFORMATION, calculé par le moteur, ou `null`. */
    equivalentAnnuel: OptimTurpeV2EquivalentAnnuel | null
    /** Cause d'un équivalent annuel absent (fenêtre trop courte, montant non servi…). */
    equivalentAnnuelCause: string | null
    reconciliation: OptimTurpeV2Reconciliation | null
    /** Le chiffre est-il un surcoût ? Se dit surcoût, jamais sous une pastille verte. */
    estSurcout: boolean
    /** Le chiffre est-il un gain mis en avant ? */
    estGain: boolean
    /** Vrai quand l'étude porte un scénario imposé (mode `price_only`) et non l'optimum. */
    estScenarioLibre: boolean
}

/** La période servie par le moteur, sinon son repli sur les dates demandées. */
export function lirePeriode(etude: OptimTurpeV2Etude): PeriodeLue {
    const servie = etude.periode ?? null
    const dateFrom = servie?.date_from ?? etude.inputs?.data?.date_from ?? null
    const dateTo = servie?.date_to ?? etude.inputs?.data?.date_to ?? null
    const joursServis = fini(servie?.jours)
    // Le décompte des jours n'est pas un montant : c'est l'écart calendaire entre
    // deux dates que le document porte. On l'admet en repli, et on le DIT, pour que
    // le montant de période garde sa durée sur une étude antérieure au lot L6.
    const jours = joursServis ?? joursEntre(dateFrom, dateTo)
    return {
        dateFrom,
        dateTo,
        jours,
        pasPrices: fini(servie?.pas_prices),
        // `est_annuelle` fait foi quand il est servi ; sinon la règle publiée
        // (365 à 366 jours) s'applique à la durée lue.
        estAnnuelle:
            typeof servie?.est_annuelle === "boolean"
                ? servie.est_annuelle
                : jours !== null && jours >= 365 && jours <= 366,
        joursDerive: joursServis === null && jours !== null,
        servie: servie !== null && servie !== undefined,
    }
}

/**
 * Déduit l'état par la règle publiée (§2.3) appliquée aux montants SERVIS, pour
 * une étude antérieure au lot du moteur.
 *
 * Ce n'est pas un état inventé : c'est l'algorithme du contrat appliqué aux
 * nombres du document, et l'appelant reçoit `etatServiParLeMoteur: false` pour
 * le dire à l'écran. Quand la nette ou la ferme manque, il n'y a pas d'état :
 * une absence n'est ni un 0 ni une « structure déjà optimale ».
 */
function deduireEtat(
    netteEur: number | null,
    fermeEur: number | null,
    seuilEur: number
): OptimTurpeV2EtatServi | null {
    if (netteEur === null) return null
    // Le surcoût et la structure déjà optimale se lisent sur la seule nette : ne
    // pas les nommer faute de connaître la structure ferme cacherait un surcoût.
    if (netteEur < -seuilEur) return "surcout"
    if (netteEur <= seuilEur) return "deja_optimale"
    // Au-dessus du seuil, c'est un gain — mais lequel ? « Économie » affirme que
    // la structure ferme est positive (§2.3). Sans elle, on ne le sait pas, et on
    // ne l'affirme pas : l'écran dit alors que l'état n'est pas disponible plutôt
    // que de promettre une économie de structure qu'il n'a pas vérifiée.
    if (fermeEur === null) return null
    return fermeEur > 0 ? "economie" : "gain_par_depassements_evites"
}

/**
 * Tout ce que la page a besoin de savoir du chiffre, lu une seule fois.
 *
 * L'ordre de lecture est toujours le même : le champ servi d'abord, le repli
 * explicite ensuite, l'absence dite en dernier recours.
 */
export function lireChiffre(etude: OptimTurpeV2Etude): ChiffreLu {
    const economie = etude.economie ?? {}
    const netteEur = fini(economie.nette_eur)
    const fermeEur = fini(economie.ferme_eur)
    const depassementsEur = fini(economie.depassements_eur)

    const seuilServi = fini(etude.etat_seuil_eur)
    const seuilEur = seuilServi ?? SEUIL_ETAT_EUR_REPLI

    const etatServi = etude.etat ?? null
    const etat = etatServi ?? deduireEtat(netteEur, fermeEur, seuilEur)

    const equivalentAnnuel = economie.equivalent_annuel ?? null
    // L'équivalent annuel n'est servi que si le moteur a produit au moins un de
    // ses montants. Un bloc qui ne porte qu'une cause n'est pas un chiffre.
    const equivalentAnnuelChiffre =
        equivalentAnnuel && fini(equivalentAnnuel.nette_eur) !== null
            ? equivalentAnnuel
            : null

    const estSurcout = etat === "surcout"

    return {
        etat,
        etatServiParLeMoteur: etatServi !== null && etatServi !== undefined,
        etatMotif: etude.etat_motif ? String(etude.etat_motif) : null,
        etatCause: etude.etat_cause ?? null,
        seuilEur,
        seuilServiParLeMoteur: seuilServi !== null,
        netteEur,
        decomposition: {
            disponible: depassementsEur !== null && fermeEur !== null,
            fermeEur,
            depassementsEur,
        },
        regimeFiscal: economie.regime_fiscal ?? null,
        montantServi:
            typeof economie.montant_servi === "boolean"
                ? economie.montant_servi
                : null,
        montantNonServiCause: economie.montant_non_servi_cause ?? null,
        periode: lirePeriode(etude),
        equivalentAnnuel: equivalentAnnuelChiffre,
        equivalentAnnuelCause: equivalentAnnuel?.cause ?? null,
        reconciliation: economie.reconciliation ?? null,
        estSurcout,
        estGain: etat !== null && ETATS_DE_GAIN.includes(etat),
        estScenarioLibre:
            etude.conduite?.mode === "scenario_libre" ||
            etude.inputs?.optimisation?.mode === "price_only",
    }
}

/**
 * Le client a-t-il imposé sa structure ? `restitution.recommandation.structure_choisie`
 * (courtage#274). Seul un `true` servi le dit : une clé absente ou fausse
 * laisse la page telle qu'elle est pour toutes les études existantes.
 */
export function lireStructureChoisie(
    etude: OptimTurpeV2Etude | null | undefined
): boolean {
    return etude?.recommandation?.structure_choisie === true
}

/**
 * La sévérité de la pastille d'état.
 *
 * Un surcoût n'est JAMAIS vert (constat F3.14) et un état absent n'est jamais
 * neutre-rassurant : il est neutre-information, et la page dit l'absence à côté.
 */
export function severiteEtat(
    etat: OptimTurpeV2EtatServi | null
): "success" | "warn" | "danger" | "info" | "secondary" {
    switch (etat) {
        case "economie":
            return "success"
        case "gain_par_depassements_evites":
            return "warn"
        case "surcout":
            return "danger"
        case "montant_non_servi":
            return "info"
        case "deja_optimale":
            return "secondary"
        default:
            return "info"
    }
}
